import {
    Component,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    output,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';

import { Searchbar, SearchbarSize } from '@ntv360/component-pantry';
import { SVG_ICONS } from '@shared/constants';

@Component({
    selector: 'app-searchbar',
    imports: [Searchbar],
    templateUrl: './searchbar.component.html',
    styleUrl: './searchbar.component.scss',
})
export class SearchbarComponent implements OnInit {
    /** Destroy ref for subscription cleanup */
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Reference to the underlying pantry searchbar.
     * Pantry no longer emits a value-change output; it exposes the current
     * value via its public `searchValue` signal, which we observe instead.
     */
    private readonly searchbarRef = viewChild.required(Searchbar);

    /** Previous value seen from the pantry searchbar, to skip no-op emissions. */
    private previousValue = '';

    /**
     * Subject for debouncing search input
     */
    private readonly searchSubject = new Subject<string>();

    constructor() {
        effect(() => {
            const value = this.searchbarRef().searchValue();
            if (value === this.previousValue) {
                return;
            }
            this.previousValue = value;
            this.onSearchValueChange(value);
        });
    }

    /**
     * Debounce time in milliseconds
     */
    public debounceTime = input<number>(300);

    /**
     * Size of the searchbar
     */
    public size = input<SearchbarSize>('md');

    /**
     * Hide the outer search button
     */
    public hideOuterSearchButton = input<boolean>(true);

    /**
     * Show clear button
     */
    public showClearButton = input<boolean>(true);

    /**
     * Minimum characters required
     */
    public minCharacters = input<number>(3);

    /**
     * Placeholder text for searchbar
     */
    public placeholder = input<string>('Search...');

    /**
     * Emits when the search input value changes (immediate)
     */
    public searchValueChange = output<string>();

    /**
     * Emits when search should be triggered (after debounce - user stops typing)
     */
    public searchTriggered = output<string>();

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Current search value
     */
    public currentValue = '';

    /**
     * Right icon
     */
    public rightIcon = input<boolean>(false);

    /**
     * Hide elements for now
     */
    public isHiddenForNow = input<boolean>(true);

    /**
     * @public
     * @description Initializes debounced search trigger stream.
     * @returns void
     */
    public ngOnInit(): void {
        this.searchSubject
            .pipe(debounceTime(this.debounceTime()), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                this.searchTriggered.emit(value);
            });
    }

    /**
     * Whether Enter key behavior should be disabled.
     */
    public disableEnterKey = input<boolean>(false);

    /**
     * @public
     * @description Handles search value changes from ntv-searchbar.
     * @param value - The new search value
     * @returns void
     */
    public onSearchValueChange(value: string): void {
        this.currentValue = value;
        this.searchValueChange.emit(value);
        this.searchSubject.next(value);
    }

    /**
     * @public
     * @description Prevents Enter key propagation for contexts that disable Enter-triggered search.
     * @param event - Enter key event
     * @returns void
     */
    public onEnterKey(event: Event): void {
        if (!this.disableEnterKey()) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (
            'stopImmediatePropagation' in event &&
            typeof event.stopImmediatePropagation === 'function'
        ) {
            event.stopImmediatePropagation();
        }
    }
}
